import { detectOS, isDesktop } from '@/lib/platform';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $fetchAllo } from '@/services/api-allo/client';
import { generate as generateKsuid } from '@langwatch/ksuid';
import {
  type AppEvent,
  type CallEventLevel,
  type CallEventName,
  type CallEventPayload,
  CallEventType,
  type CreateAppEventsBody,
} from './types';

/** Events that represent a new call entering the system. */
const SESSION_OPENING_EVENTS: Set<string> = new Set(['call_received', 'attempt_call']);

/** Events that represent a call leaving the system. */
const SESSION_CLOSING_EVENTS: Set<string> = new Set(['status_completed']);

/** Events that ride on their own one-shot session, decoupled from any active call. */
const STANDALONE_EVENTS: Set<string> = new Set(['feedback_reported']);

class TracingService {
  private sessionId: string | null = null;
  /**
   * Trace id (`trc-…`) minted at `attempt_call`, held until the outbound leg
   * id lands at `status_ringing` so the in-between `attempt_call` event can
   * still carry it. Null once bound or when no outbound call is starting.
   */
  private pendingTraceId: string | null = null;
  /**
   * Outbound leg id → trace id. One entry per outbound call, so overlapping
   * calls keep distinct trace ids and inbound legs (never added here) carry
   * none.
   */
  private readonly traceIdByCallId = new Map<string, string>();
  private activeCallCount = 0;
  private callEndPayload: CallEventPayload | null = null;
  /**
   * Vonage region ('US' | 'EU' | 'AP') resolved once at boot. Held here so the
   * call logger can facet every New Relic call log by region without importing
   * the heavy Vonage client provider (which imports the call logger back).
   */
  private vonageRegion: string | null = null;
  /** Current Vonage SDK session id, mirrored here for the same reason as the region. */
  private vonageSessionId: string | null = null;
  /**
   * Platform/version fields are resolved lazily on the first event rather than
   * in the constructor: this module is imported transitively by the call logger
   * (for trace correlation), so eager `isDesktop()`/`detectOS()` calls would run
   * at import time and break any consumer that partially mocks `@/lib/platform`.
   */
  private platformName?: 'macos' | 'windows' | 'web';
  private platformOs?: string;
  private appVersion?: string;

  trackEvent(eventName: CallEventName, level: CallEventLevel, payload?: CallEventPayload): void {
    this.sendEvent({ eventName, level, payload });
  }

  /** Buffer quality data to be merged into the next status_completed event payload. */
  setCallEndPayload(payload: CallEventPayload): void {
    this.callEndPayload = payload;
  }

  /**
   * Trace id for a known outbound call leg, for correlating New Relic call logs
   * with the backend events stream. Returns undefined for inbound/unknown legs
   * and for logs without a leg id — only confirmed outbound legs are correlated.
   */
  getCallTraceId(callId?: string): string | undefined {
    return callId ? this.traceIdByCallId.get(callId) : undefined;
  }

  /**
   * Trace id for the outbound call currently starting up, before its leg id is
   * known. Minted at `attempt_call` and held until the leg id lands at
   * `status_ringing` (after which it moves to `traceIdByCallId` and this
   * returns undefined). Lets the call placer forward it to the SIP backend as
   * server-call custom data so the leg can be joined to the events stream.
   */
  getPendingTraceId(): string | undefined {
    return this.pendingTraceId ?? undefined;
  }

  /** Records the Vonage region resolved at boot so call logs can be faceted by it. */
  setVonageRegion(region: string | null): void {
    this.vonageRegion = region;
  }

  /** The current session's Vonage region, or undefined before it's resolved. */
  getVonageRegion(): string | undefined {
    return this.vonageRegion ?? undefined;
  }

  /** Records the current Vonage SDK session id (or null when the session is cleared). */
  setVonageSessionId(sessionId: string | null): void {
    this.vonageSessionId = sessionId;
  }

  /** The current Vonage SDK session id, or undefined when there is no session. */
  getVonageSessionId(): string | undefined {
    return this.vonageSessionId ?? undefined;
  }

  private createSession(): string {
    this.sessionId = `ses-${crypto.randomUUID()}`;
    return this.sessionId;
  }

  private getOrCreateSession(): string {
    return this.sessionId ?? this.createSession();
  }

  private sendEvent({
    eventName,
    level,
    payload,
  }: {
    eventName: CallEventName;
    level: CallEventLevel;
    payload?: CallEventPayload;
  }): void {
    const isStandalone = STANDALONE_EVENTS.has(eventName);

    // Start a new session only when no active calls exist
    if (!isStandalone && SESSION_OPENING_EVENTS.has(eventName)) {
      if (this.activeCallCount === 0) {
        this.createSession();
      }
      this.activeCallCount++;
    }

    // Merge buffered quality data into status_completed
    if (!isStandalone && eventName === 'status_completed' && this.callEndPayload) {
      payload = { ...this.callEndPayload, ...payload };
      this.callEndPayload = null;
    }

    const sessionId = isStandalone ? `fdb-${crypto.randomUUID()}` : this.getOrCreateSession();
    const callTraceId = isStandalone ? undefined : this.resolveCallTraceId(eventName, payload);

    const event: AppEvent = {
      eventName,
      eventLevel: level,
      eventType: CallEventType.Call,
      eventPayload: payload ?? null,
      eventAt: new Date().toISOString(),
    };
    if (callTraceId) {
      event.callTraceId = callTraceId;
    }

    const body: CreateAppEventsBody = {
      sessionId,
      platformName: this.getPlatformName(),
      platformOs: this.getPlatformOs(),
      appVersion: this.getAppVersion(),
      events: [event],
    };

    // `/v1/app/events` isn't in the camelCase `api-allo-remote` spec yet, so it
    // still routes through `$fetchAllo`, whose generated `api-allo-v1` types
    // describe the pre-migration snake_case contract. The backend now accepts
    // the camelCase body; bridge the stale generated type here until the
    // endpoint is regenerated into the remote spec and this can move to
    // `$fetchAlloRemote` with the cast dropped.
    $fetchAllo
      .POST('/v1/app/events', {
        body: body as unknown as components['schemas']['CreateAppEventsRequest'],
      })
      .catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('[TracingService] Failed to send event:', eventName, error);
        }
      });

    // status_completed terminates a call. Trace cleanup is deferred to a
    // microtask so events fired synchronously after it (device_call_ended,
    // device_call_failed for the same leg) still resolve the trace id.
    if (!isStandalone && SESSION_CLOSING_EVENTS.has(eventName)) {
      this.activeCallCount = Math.max(0, this.activeCallCount - 1);
      const callId = this.callIdFrom(payload);
      queueMicrotask(() => {
        if (callId) {
          this.traceIdByCallId.delete(callId);
        } else {
          // Outbound call that failed before its leg id was known.
          this.pendingTraceId = null;
        }
        if (this.activeCallCount === 0) {
          this.sessionId = null;
        }
      });
    }
  }

  /**
   * Returns the `trc-…` trace id for an outbound event, or undefined for
   * inbound and non-call events. Trace ids are unique per outbound leg, so an
   * inbound call accepted mid-outbound never inherits the outbound trace.
   */
  private resolveCallTraceId(
    eventName: CallEventName,
    payload: CallEventPayload | undefined,
  ): string | undefined {
    const callId = this.callIdFrom(payload);

    // Mint at outbound start; the leg id isn't known yet, so hold it pending.
    if (eventName === 'attempt_call') {
      this.pendingTraceId = `trc-${generateKsuid('trc').toString().split('_')[1]}`;
    }

    // Bind the pending trace id to the first outbound event carrying a leg id.
    if (
      callId &&
      payload?.direction === 'outbound' &&
      this.pendingTraceId &&
      !this.traceIdByCallId.has(callId)
    ) {
      this.traceIdByCallId.set(callId, this.pendingTraceId);
      this.pendingTraceId = null;
    }

    // Once an event carries a leg id, the map is authoritative. Inbound legs
    // are never added to it, so an inbound call accepted mid-outbound can never
    // inherit a trace id — even though its events carry a stale
    // `direction: 'outbound'` from the still-active outbound call's state.
    if (callId) {
      return this.traceIdByCallId.get(callId);
    }

    // No leg id yet: only the outbound call currently starting up carries the
    // pending trace (attempt_call itself, or a failure before status_ringing).
    if (eventName === 'attempt_call' || payload?.direction === 'outbound') {
      return this.pendingTraceId ?? undefined;
    }
    return undefined;
  }

  private callIdFrom(payload: CallEventPayload | undefined): string | undefined {
    return typeof payload?.deviceLegId === 'string' ? payload.deviceLegId : undefined;
  }

  private getPlatformName(): 'macos' | 'windows' | 'web' {
    this.platformName ??= this.resolvePlatformName();
    return this.platformName;
  }

  private getPlatformOs(): string {
    this.platformOs ??= this.resolvePlatformOs();
    return this.platformOs;
  }

  private getAppVersion(): string {
    this.appVersion ??= typeof APP_VERSION !== 'undefined' ? APP_VERSION : 'unknown';
    return this.appVersion;
  }

  private resolvePlatformName(): 'macos' | 'windows' | 'web' {
    if (isDesktop()) {
      const os = detectOS();
      if (os === 'mac') {
        return 'macos';
      }
      if (os === 'windows') {
        return 'windows';
      }
    }
    return 'web';
  }

  private resolvePlatformOs(): string {
    const os = detectOS();
    if (os === 'mac') {
      return 'macOS';
    }
    if (os === 'windows') {
      return 'Windows';
    }
    return 'Web';
  }
}

export const tracingService = new TracingService();
